import { doc, getDoc, updateDoc, serverTimestamp, writeBatch } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Create a new user profile document in Firestore after Firebase Auth signup.
 * Also writes to the usernames collection (publicly readable) for availability checks.
 */
export async function createUserProfile(uid, { username, email }) {
  const batch = writeBatch(db);
  batch.set(doc(db, 'users', uid), {
    username,
    email,
    role: 'user',
    darkMode: false,
    joinedAt: serverTimestamp(),
  });
  batch.set(doc(db, 'usernames', username.toLowerCase()), { uid });
  await batch.commit();
}

export async function getUserProfile(uid) {
  const snap = await getDoc(doc(db, 'users', uid));
  return snap.exists() ? { uid, ...snap.data() } : null;
}

export async function updateUserProfile(uid, data) {
  await updateDoc(doc(db, 'users', uid), data);
}

/**
 * Check if a username is available (case-insensitive).
 * Queries the publicly-readable usernames collection — works without auth.
 */
export async function isUsernameAvailable(username) {
  if (!username || username.length < 3) return false;
  const snap = await getDoc(doc(db, 'usernames', username.toLowerCase()));
  return !snap.exists();
}

const USERNAME_REGEX = /^[a-zA-Z0-9_]{3,20}$/;

/**
 * Change a user's username. Atomically removes the old username entry,
 * creates the new one, and updates the user doc.
 * Throws if the new username is invalid or already taken.
 */
export async function changeUsername(uid, oldUsername, newUsername) {
  const trimmed = newUsername.trim();
  if (!USERNAME_REGEX.test(trimmed)) {
    throw new Error('Username must be 3–20 characters: letters, numbers, underscores only.');
  }
  if (trimmed.toLowerCase() === oldUsername.toLowerCase()) {
    throw new Error('That is already your username.');
  }
  const available = await isUsernameAvailable(trimmed);
  if (!available) throw new Error('Username is already taken.');

  const batch = writeBatch(db);
  batch.delete(doc(db, 'usernames', oldUsername.toLowerCase()));
  batch.set(doc(db, 'usernames', trimmed.toLowerCase()), { uid });
  batch.update(doc(db, 'users', uid), { username: trimmed });
  await batch.commit();
}

/**
 * Fetch a map of userId → username for an array of user IDs.
 * Falls back to first 6 chars of uid if user doc is missing.
 */
export async function getUsernamesMap(userIds) {
  if (!userIds.length) return {};
  const docs = await Promise.all(userIds.map((uid) => getDoc(doc(db, 'users', uid))));
  return Object.fromEntries(
    userIds.map((uid, i) => [uid, docs[i].exists() ? docs[i].data().username : uid.slice(0, 6)])
  );
}
